import React from "react";
import { format } from "date-fns";
import { useLocation, useHistory } from "react-router-dom";

export function formatAmount(amount) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "EUR"
    }).format(amount);
}

export function formatNumber(number) {
    return new Intl.NumberFormat("en-US").format(number);
}

export const CAMPAIGN_PRODUCT_IDS = ["40f3dc4b-3bd6-e511-80d3-000d3ab18bd8", "41f3dc4b-3bd6-e511-80d3-000d3ab18bd8", "b9d5478c-592a-4f89-9c78-e888d836c24c"];

export const STATUS = {
    PRE_ACKNOWLEDGED: "Pre-Acknowledged",
    ACKNOWLEDGED: "Acknowledged",
    APPROVED: "Approved",
    CANCELLED: "Cancelled",
    ACTIVE: "Active",
    SHIPPED: "Shipped"
};

export const STATUS_STYLE_MAP = {
    [STATUS.PRE_ACKNOWLEDGED]: {
        dotColor: "#217BBE",
        backgroundColor: "#217BBE26"
    },
    [STATUS.APPROVED]: {
        dotColor: "#00A76A",
        backgroundColor: "#00A76A26"
    },
    [STATUS.CANCELLED]: {
        dotColor: "#D22630",
        backgroundColor: "#D2263026"
    }
};

export const ORDER_ITEM_STATUS_MAP = {
    [STATUS.ACTIVE]: {
        dotColor: "#217BBE",
        backgroundColor: "#217BBE26",
        label: "Order received"
    },
    [STATUS.ACKNOWLEDGED]: {
        dotColor: "#FF9C00",
        backgroundColor: "#FF9C0026",
        label: "Order processing"
    },
    [STATUS.SHIPPED]: {
        dotColor: "#00A76A",
        backgroundColor: "#00A76A26",
        label: "Delivery"
    }
};

/**
 * @param {Record<string,any>} params
 * @returns {{
 *  name: string,
 *  price: string,
 *  areas: number,
 *  addresses: number,
 *  deliveryDate: string
 * }}
 */
export function parseOrderItem(params) {
    const customerDataItemId = params.CustomerDataItem.Id;
    const image = params.CustomerDataItem.Image;
    const name = params.CustomerDataItem.Product.Name;
    const price = formatAmount(params.Price.TotalPriceExVat);
    const isUsingCustomDatabase = params.CustomerDataItem?.Targeting?.MapData?.FromCSV;
    const areas = isUsingCustomDatabase ? 1 : JSON.parse(params.CustomerDataItem?.Targeting?.MapData?.TargetingRequestData || "{}").geoJsonString?.geographyFilter?.features?.length;
    const addresses = formatNumber(Number(params?.CustomerDataItem?.Targeting?.MapData?.ResidentialCount || 0) + Number(params?.CustomerDataItem?.Targeting?.MapData?.CommercialCount || 0));
    const deliveryDate = format(new Date(params.CustomerDataItem.DeliveryDate), "dd MMM yyyy");
    const status = params.Status?.Name;
    const hasFreeCopy = Object.values(params?.CustomerDataItem?.Targeting?.AdditionalAddress || {}).some(value => !!value);
    const additionalCopies = params?.CustomerDataItem?.AdditionalCopies || {};
    const additionalCopiesQuantity = additionalCopies?.CustomerDataItem?.Quantity || 0;
    const hasAdditionalCopies = Object.values(additionalCopies).length > 0 && additionalCopiesQuantity > 0;
    const additionalCopiesPrice = hasAdditionalCopies ? additionalCopies?.Price?.TotalPriceExVat : 0;
    const customerDataItemAdditionalCopies = hasAdditionalCopies
        ? {
              noOfCopies: formatNumber(additionalCopiesQuantity),
              price: formatAmount(additionalCopiesPrice)
          }
        : {};

    return { customerDataItemId, name, price, areas, addresses, deliveryDate, image, status, hasFreeCopy, additionalCopies: customerDataItemAdditionalCopies };
}

export function constructImagePath({ uId, bookId, file }) {
    return `customerdata/${uId}/Admailer/${bookId}/${file}`;
}

export function useSearchParams() {
    const location = useLocation();
    const history = useHistory();

    const searchParams = new URLSearchParams(location.search);

    const setSearchParam = (param, value) => {
        searchParams.set(param, value);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        history.push(newUrl);
    };

    const removeSearchParam = param => {
        searchParams.delete(param);
        const newUrl = searchParams.toString() ? `${location.pathname}?${searchParams.toString()}` : location.pathname;
        history.push(newUrl);
    };

    const getSearchParam = param => {
        return searchParams.get(param);
    };

    const params = React.useMemo(() => {
        return Array.from(searchParams.entries());
    }, [location.search]);

    return { getSearchParam, setSearchParam, removeSearchParam, searchParams: params };
}
